import { RichText } from 'prismic-reactjs';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import ColoredSlice from '@monofront/design-system/organisms/ColoredSlice';
import PrismicLink from '@components/PrismicLink';
import { useMediaQuery } from '@monofront/business/hooks';
import withSlice from '@screens/SlicesContainer/withSlice';
import { mapEditorialListAndImage } from './mapEditorialListAndImage';
import { IEditorialListAndImage } from './types';
import { PrismicTitle } from '@components/PrismicTitle';
import { isFilled } from '@prismicio/client';
import { PrismicRichText } from '@components/PrismicRichText';
import cn from 'classnames';

const EditorialListAndImage: React.FC<IEditorialListAndImage> = ({
  items,
  primary,
}) => {
  const isMobile = useMediaQuery(800);

  if (
    !primary ||
    ((!items || !items.length) && !('content' in primary)) ||
    !isFilled.richText(primary.title) ||
    !isFilled.select(primary.titleStyle)
  )
    return null;

  const title = (
    <PrismicTitle
      field={primary.title}
      titleStyleField={primary.titleStyle}
      titleDecorationElement={
        isFilled.image(primary.titleIcon) ? (
          <span className="-tw-ml-3 tw-align-[0.3em]">
            <ResponsiveImage
              width={primary.titleIcon.dimensions.width}
              height={primary.titleIcon.dimensions.height}
              src={primary.titleIcon.url}
              alt={primary.titleIcon.alt ?? undefined}
            />
          </span>
        ) : undefined
      }
    />
  );

  const textContent = (
    <div className="tw-flex tw-h-full tw-flex-col tw-justify-center tw-gap-4">
      {'content' in primary && (
        <PrismicRichText
          field={primary.content}
          textAlignmentField={primary.textAlignment}
          classNames={{
            list: 'tw-lf-bullet-list',
            listItem: cn(
              'tw-lf-bullet-list-item',
              primary.listBulletpointColor
                ? `before:tw-bg-[${primary.listBulletpointColor}`
                : '',
            ),
          }}
        />
      )}
      {items?.length ? (
        <ul className="tw-lf-bullet-list tw-mt-0 ">
          {items.map((item, i) => (
            <li
              key={i}
              className={`tw-lf-bullet-list-item ${
                primary.listBulletpointColor
                  ? `before:tw-bg-[${primary.listBulletpointColor}`
                  : ''
              }`}
            >
              <RichText
                render={item.listItem}
                htmlSerializer={(type, _element, _text, children, index) =>
                  type === 'strong' ? (
                    <strong className="tw-font-bold" key={index}>
                      {children}
                    </strong>
                  ) : null
                }
              />
            </li>
          ))}
        </ul>
      ) : null}
      {primary.ctaLink && primary.ctaLabel && (
        <div className="tw-flex tw-flex-col">
          <PrismicLink
            className={cn('tw-lf-button tw-lf-button-primary tw-lf-button-md', {
              'tw-self-start':
                !('textAlignment' in primary) ||
                primary.textAlignment !== 'center',
              'tw-self-center':
                ('textAlignment' in primary &&
                  primary.textAlignment === 'center') ||
                isMobile,
            })}
            link={primary.ctaLink}
            trackingId={primary.ctaTrackingId || undefined}
          >
            {primary.ctaLabel}
          </PrismicLink>
        </div>
      )}
    </div>
  );

  const imageContent = isMobile
    ? primary.mobileImage && (
        <div className="image-wrapper">
          <ResponsiveImage
            width={primary.mobileImage.dimensions.width}
            height={primary.mobileImage.dimensions.height}
            src={primary.mobileImage.url}
            alt={primary.mobileImage.alt}
          />
        </div>
      )
    : primary.desktopImage && (
        <div className="image-wrapper">
          <ResponsiveImage
            width={primary.desktopImage.dimensions.width}
            height={primary.desktopImage.dimensions.height}
            src={primary.desktopImage.url}
            alt={primary.desktopImage.alt}
          />
        </div>
      );

  return (
    <>
      <ColoredSlice
        backgroundColor={primary.backgroundColor}
        title={title}
        content={textContent}
        secondContent={imageContent}
        areContentsReversed={primary.revertTextImagePosition}
        UNSAFE_className="editorial-list-and-image"
      />
      <style jsx>{`
        :global(.editorial-list-and-image .left-column-cta) {
          display: inline-flex;
          margin-top: var(--space-4);
        }

        @media (max-width: 800px) {
          :global(.editorial-list-and-image .cta-wrapper) {
            text-align: center;
          }
          :global(.editorial-list-and-image .image-wrapper) {
            margin: 0 auto;
            max-width: 345px;
          }
        }
      `}</style>
    </>
  );
};

export default withSlice(EditorialListAndImage, mapEditorialListAndImage);
